import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { LESSON } from "@/constants/testIds";
import { AskAnywhereProvider } from "@/components/AskAnywhere";
import { CodeBlock } from "@/components/CodeBlock";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Lightbulb, Sparkles, Code2, ListChecks, Target, BookOpen,
  RefreshCw, Star, MessageCircle,
} from "lucide-react";

export default function LessonPage() {
  const { slug, index } = useParams();
  const idx = parseInt(index, 10);
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuiz, setShowQuiz] = useState(false);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [path, setPath] = useState(null);
  const [dynamicQuestions, setDynamicQuestions] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [quizLoading, setQuizLoading] = useState(false);
  const [lessonRating, setLessonRating] = useState(0);
  const [lessonComment, setLessonComment] = useState("");
  const [ratingSent, setRatingSent] = useState(false);
  const [practiceMode, setPracticeMode] = useState(false);  // Unlimited unscored practice

  useEffect(() => {
    setLoading(true);
    setLesson(null);
    setShowQuiz(false);
    setAnswers({});
    setResult(null);
    setDynamicQuestions(null);
    setRetryCount(0);
    setLessonRating(0);
    setLessonComment("");
    setRatingSent(false);
    setPracticeMode(false);
    api.post(`/paths/${slug}/enroll`).catch(() => {});
    Promise.all([
      api.get(`/paths/${slug}/lessons/${idx}`),
      api.get(`/paths/${slug}`),
    ]).then(([l, p]) => {
      setLesson(l.data);
      setPath(p.data);
    }).catch(() => toast.error("Could not load lesson"))
      .finally(() => setLoading(false));
  }, [slug, idx]);

  const complete = async () => {
    setCompleting(true);
    try {
      await api.post("/lessons/complete", { path_slug: slug, lesson_index: idx });
      toast.success("Lesson complete!");
      const total = path?.lessons?.length || 0;
      if (idx + 1 < total) navigate(`/app/paths/${slug}/lessons/${idx + 1}`);
      else navigate(`/app/paths/${slug}`);
    } finally { setCompleting(false); }
  };

  const currentQuestions = dynamicQuestions || (lesson?.mini_quiz || []);

  const submitQuiz = async () => {
    if (!lesson) return;
    const qs = currentQuestions;
    let correct = 0;
    qs.forEach((q, i) => { if (answers[i] === q.answer_index) correct++; });
    setResult({ score: correct, total: qs.length, practice: practiceMode });
    try {
      await api.post("/quizzes/submit", {
        path_slug: slug, lesson_index: idx, score: correct, total: qs.length,
        practice: practiceMode,
      });
    } catch { /* silent — result stays on screen */ }
  };

  const retakeQuiz = async () => {
    setQuizLoading(true);
    try {
      const next = retryCount + 1;
      const { data } = await api.post("/quizzes/generate", {
        path_slug: slug, lesson_index: idx, difficulty: "medium", retry_count: next,
        practice: practiceMode,
      });
      setDynamicQuestions(data.questions || []);
      setRetryCount(next);
      setAnswers({});
      setResult(null);
      toast.success(practiceMode ? "Practice set ready — no pressure" : "Fresh questions — same topic, new angles");
    } catch { toast.error("Could not regenerate quiz"); }
    finally { setQuizLoading(false); }
  };

  const enterPracticeMode = async () => {
    setQuizLoading(true);
    try {
      const { data } = await api.post("/quizzes/generate", {
        path_slug: slug, lesson_index: idx, difficulty: "medium",
        retry_count: retryCount + 1, practice: true,
      });
      setDynamicQuestions(data.questions || []);
      setRetryCount(retryCount + 1);
      setAnswers({});
      setResult(null);
      setPracticeMode(true);
      setShowQuiz(true);
      toast.success("Practice mode on — take as many as you want, nothing counts");
    } catch { toast.error("Could not start practice"); }
    finally { setQuizLoading(false); }
  };

  const sendLessonRating = async () => {
    if (lessonRating === 0) { toast.error("Pick a star rating"); return; }
    try {
      await api.post("/feedback", {
        kind: "lesson",
        message: lessonComment || `Rated ${lessonRating}/5`,
        rating: lessonRating,
        page: window.location.pathname,
        lesson_slug: slug,
        lesson_index: idx,
        lesson_title: lesson?.title,
        path_slug: slug,
        path_title: path?.title,
        user_agent: navigator.userAgent.slice(0, 300),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      });
      setRatingSent(true);
      toast.success("Thanks for the feedback!");
    } catch { toast.error("Could not send rating"); }
  };

  if (loading || !lesson) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="label-mono">Generating lesson with AI…</div>
        <div className="h-8 w-2/3 rounded bg-secondary" />
        <div className="h-4 w-full rounded bg-secondary" />
        <div className="h-4 w-5/6 rounded bg-secondary" />
        <div className="h-40 w-full rounded bg-secondary" />
      </div>
    );
  }

  const total = path?.lessons?.length || 0;
  // Detect a sensible highlighting language for example code blocks from the path
  const pathLang = ((path?.category || "").toLowerCase().includes("web") ? "javascript"
                  : (path?.category || "").toLowerCase().includes("data") ? "python"
                  : "python");

  return (
    <div data-testid={LESSON.root} className="space-y-8 animate-fade-up">
      <div className="flex items-center justify-between gap-4">
        <Link to={`/app/paths/${slug}`} className="inline-flex items-center gap-2 text-xs font-mono text-muted-foreground hover:text-primary">
          <ArrowLeft className="h-3.5 w-3.5" /> {path?.title || "Path"}
        </Link>
        <div className="flex items-center gap-3">
          <div className="hidden sm:block label-mono text-primary">💡 Highlight any text to ask your mentor</div>
          <div className="label-mono">Lesson {idx + 1} / {total}</div>
        </div>
      </div>

      <AskAnywhereProvider contextLabel={`${path?.title || ""} · ${lesson.title}`}>

      <div>
        <div className="label-mono text-primary">// lesson</div>
        <h1 className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">{lesson.title}</h1>
      </div>

      {/* Objectives */}
      <section className="rounded-2xl border border-border bg-card p-6">
        <div className="mb-3 flex items-center gap-2"><Target className="h-4 w-4 text-primary" /><h2 className="font-display text-lg font-bold">Objectives</h2></div>
        <ul className="grid gap-2 sm:grid-cols-2">
          {(lesson.objectives || []).map((o, i) => (
            <li key={i} className="flex items-start gap-2 rounded-lg border border-border bg-background p-3 text-sm">
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-accent" /> {o}
            </li>
          ))}
        </ul>
      </section>

      {/* Theory + Analogy */}
      <section className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 rounded-2xl border border-border bg-card p-6">
          <div className="mb-3 flex items-center gap-2"><BookOpen className="h-4 w-4 text-primary" /><h2 className="font-display text-lg font-bold">Theory</h2></div>
          <div className="space-y-3 text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap">{lesson.theory}</div>
        </div>
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6">
          <div className="mb-3 flex items-center gap-2"><Lightbulb className="h-4 w-4 text-primary" /><h2 className="font-display text-lg font-bold">Analogy</h2></div>
          <p className="text-sm leading-relaxed">{lesson.analogy}</p>
        </div>
      </section>

      {/* Examples */}
      {(lesson.examples || []).length > 0 && (
        <section className="rounded-2xl border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-2"><Code2 className="h-4 w-4 text-primary" /><h2 className="font-display text-lg font-bold">Examples</h2></div>
          <div className="grid gap-4 md:grid-cols-2">
            {(lesson.examples || []).map((ex, i) => (
              <div key={i} className="rounded-xl border border-border bg-background p-4">
                <div className="label-mono">Example {i + 1}</div>
                <div className="mt-1 font-display font-bold">{ex.title}</div>
                <div className="mt-2 text-sm text-muted-foreground">{ex.description}</div>
                {ex.code && (
                  <div className="mt-3">
                    <CodeBlock code={ex.code} language={ex.language || pathLang} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Exercise */}
      {lesson.exercise && (
        <section className="rounded-2xl border border-accent/40 bg-accent/5 p-6">
          <div className="mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent" /><h2 className="font-display text-lg font-bold">Try it</h2></div>
          <div className="text-sm">{lesson.exercise.prompt}</div>
          <details className="mt-3">
            <summary className="cursor-pointer text-xs font-mono uppercase tracking-widest text-accent">Hint</summary>
            <div className="mt-2 text-sm text-muted-foreground">{lesson.exercise.hint}</div>
          </details>
          <details className="mt-2">
            <summary className="cursor-pointer text-xs font-mono uppercase tracking-widest text-accent">Solution</summary>
            <div className="mt-2">
              {typeof lesson.exercise.solution === "string" && /\n/.test(lesson.exercise.solution) ? (
                <CodeBlock code={lesson.exercise.solution} language={pathLang} />
              ) : (
                <div className="text-sm text-muted-foreground whitespace-pre-wrap">{lesson.exercise.solution}</div>
              )}
            </div>
          </details>
        </section>
      )}

      {/* Mini quiz */}
      <section className="rounded-2xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <ListChecks className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-bold">Mini quiz</h2>
            {retryCount > 0 && <span className="label-mono text-primary">retry #{retryCount}</span>}
            {practiceMode && (
              <span
                data-testid="lesson-practice-badge"
                className="rounded-full border border-accent/50 bg-accent/10 px-2.5 py-0.5 font-mono text-[0.6rem] uppercase tracking-widest text-accent"
              >
                Practice mode · unscored
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {result && (
              <Button
                onClick={retakeQuiz}
                disabled={quizLoading}
                variant="outline"
                size="sm"
                className="rounded-full font-mono text-xs uppercase tracking-widest"
                data-testid="lesson-quiz-retry-btn"
              >
                <RefreshCw className={`mr-2 h-3.5 w-3.5 ${quizLoading ? "animate-spin" : ""}`} />
                {quizLoading ? "Fresh set…" : "Try again — fresh questions"}
              </Button>
            )}
            {!practiceMode && (
              <Button
                onClick={enterPracticeMode}
                disabled={quizLoading}
                variant="outline"
                size="sm"
                data-testid="lesson-practice-btn"
                className="rounded-full font-mono text-xs uppercase tracking-widest border-accent/50 text-accent hover:bg-accent/10"
              >
                <Sparkles className="mr-2 h-3.5 w-3.5" /> Practice unlimited
              </Button>
            )}
            {!showQuiz && (
              <Button data-testid={LESSON.quizStartBtn} onClick={() => setShowQuiz(true)} variant="outline" className="rounded-full font-mono text-xs uppercase tracking-widest">
                Start quiz
              </Button>
            )}
          </div>
        </div>
        {showQuiz && (
          <div className="space-y-4">
            {currentQuestions.map((q, qi) => (
              <div key={`${retryCount}-${qi}`} className="rounded-xl border border-border p-4">
                <div className="label-mono">Q{qi + 1}</div>
                <div className="mt-1 font-medium">{q.question}</div>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {q.options.map((op, oi) => {
                    const selected = answers[qi] === oi;
                    const showCorrect = result && oi === q.answer_index;
                    const showWrong = result && selected && oi !== q.answer_index;
                    return (
                      <button
                        key={oi}
                        data-testid={LESSON.quizOption(qi, oi)}
                        onClick={() => !result && setAnswers((a) => ({ ...a, [qi]: oi }))}
                        className={`rounded-lg border px-3 py-2 text-left text-sm transition ${showCorrect ? "border-accent bg-accent/10" : showWrong ? "border-destructive bg-destructive/10" : selected ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
                      >
                        {op}
                      </button>
                    );
                  })}
                </div>
                {result && (
                  <div className="mt-2 text-xs text-muted-foreground">{q.explanation}</div>
                )}
              </div>
            ))}
            {!result ? (
              <Button data-testid={LESSON.quizSubmit} onClick={submitQuiz} className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest">Submit answers</Button>
            ) : (
              <div className="rounded-xl border border-primary/40 bg-primary/5 p-4">
                <div className="label-mono">{practiceMode ? "Practice result · not saved" : "Result"}</div>
                <div className="mt-1 font-display text-2xl font-black">{result.score} / {result.total} correct</div>
                <div className="mt-2 label-mono">
                  {practiceMode
                    ? "No pressure — this doesn't count toward XP or streaks. Tap 'Try again' for more."
                    : "Want more practice? Tap \"Try again — fresh questions\" above for new problems on the same objectives."}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Summary */}
      {lesson.summary && (
        <section className="rounded-2xl border border-border bg-card p-6">
          <div className="label-mono">// summary</div>
          <p className="mt-2 text-sm">{lesson.summary}</p>
          {(lesson.next_steps || []).length > 0 && (
            <div className="mt-4">
              <div className="label-mono">// next steps</div>
              <ul className="mt-2 flex flex-wrap gap-2">
                {lesson.next_steps.map((n, i) => (
                  <li key={i} className="rounded-full border border-border bg-background px-3 py-1 font-mono text-xs">{n}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* End-of-lesson rating */}
      <section data-testid="lesson-rating-card" className="rounded-2xl border border-primary/30 bg-primary/5 p-6">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg font-bold">How was this lesson?</h2>
        </div>
        {ratingSent ? (
          <div className="mt-3 rounded-lg border border-accent/40 bg-accent/10 p-3 text-sm">
            Thanks — we use every rating to improve LaneX.
          </div>
        ) : (
          <>
            <div className="mt-3 flex items-center gap-1">
              {[1,2,3,4,5].map((n) => (
                <button
                  key={n}
                  onClick={() => setLessonRating(n)}
                  aria-label={`${n} star`}
                  data-testid={`lesson-rating-star-${n}`}
                  className="rounded-full p-1"
                >
                  <Star className={`h-6 w-6 transition ${n <= lessonRating ? "fill-accent text-accent" : "text-muted-foreground"}`} />
                </button>
              ))}
              <span className="ml-2 label-mono">{lessonRating ? `${lessonRating}/5` : "tap to rate"}</span>
            </div>
            <Textarea
              value={lessonComment}
              onChange={(e) => setLessonComment(e.target.value)}
              placeholder="Optional — what worked, what was unclear?"
              className="mt-3 min-h-[70px] rounded-lg"
              data-testid="lesson-rating-comment"
            />
            <div className="mt-3">
              <Button onClick={sendLessonRating} disabled={lessonRating === 0} className="rounded-full font-mono text-xs uppercase tracking-widest" variant="outline" data-testid="lesson-rating-submit">
                Send rating
              </Button>
            </div>
          </>
        )}
      </section>

      {/* Actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={() => navigate(`/app/tutor`)} data-testid={LESSON.askTutorBtn} className="rounded-full font-mono text-xs uppercase tracking-widest">
          <Sparkles className="mr-2 h-4 w-4" /> Ask your Mentor
        </Button>
        <Button data-testid={LESSON.completeBtn} onClick={complete} disabled={completing} className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest">
          {completing ? "Saving…" : idx + 1 < total ? "Complete & continue" : "Complete lesson"}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      </AskAnywhereProvider>
    </div>
  );
}
